import { fabric } from 'fabric';
import { getObjectsByType } from '../canvas';
import { CanvasDataTypes } from './canvas-data-types';

/**
 * PRCM-10916: Resolves the id of the photo zone a fabric drag/drop event is
 * targeting so a drop (or drag-over) can be routed to that specific zone
 * instead of free-placing the image on the canvas.
 *
 * Resolution order:
 *  1. `event.target` is the photo zone itself (`data.type === PhotoZone`,
 *     matched by its `name`).
 *  2. `event.target` is the zone's add-photo (+) button, which carries
 *     `data.photoZoneId` pointing back at the zone it belongs to.
 *  3. Fallback hit-test: walk every photo zone on the canvas and return the
 *     first one whose bounding box contains the pointer for this event. This
 *     covers browsers/situations where fabric does not populate
 *     `event.target` for native HTML5 drag events (e.g. `dragover`).
 *
 * @param canvas - The fabric canvas the event occurred on
 * @param event - The fabric mouse/drag event (`drop`, `dragover`, etc.)
 * @returns The matched photo zone's id (its fabric `name`), or `null` if the
 * event did not land on any photo zone (caller should ignore the drop).
 */
export const resolvePhotoZoneIdFromEvent = (
  canvas: fabric.Canvas | null | undefined,
  event: fabric.IEvent<Event>,
): string | null => {
  if (!canvas) {
    return null;
  }

  const target = event.target as fabric.Object | undefined;

  if (target?.data?.type === CanvasDataTypes.PhotoZone && target.name) {
    return target.name;
  }

  if (target?.data?.type === CanvasDataTypes.PhotoZoneButton && target.data?.photoZoneId) {
    return target.data.photoZoneId as string;
  }

  // Occupied zone: the drop lands on the existing zone image, which points back
  // at its zone via `data.photoZoneId` (the underlying PhotoZone rect is set to
  // `visible: false` once filled). Resolve it directly so a replace-drop does
  // not depend on the hit-test below.
  if (target?.data?.type === CanvasDataTypes.PhotoZoneImage && target.data?.photoZoneId) {
    return target.data.photoZoneId as string;
  }

  const pointer = canvas.getPointer(event.e);
  const pointerPoint = new fabric.Point(pointer.x, pointer.y);
  const hitZone = getObjectsByType(canvas, CanvasDataTypes.PhotoZone).find((photoZone) =>
    photoZone.containsPoint(pointerPoint),
  );

  return hitZone?.name ?? null;
};
